import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { KpiStrip } from "@/components/ui/kpi-strip";
import { Segmented } from "@/components/ui/segmented";
import {
  llmUsageApi,
  BREAKDOWN_DIMS,
  type BreakdownDim,
  type BudgetRow,
  type AccountUsageRow,
  type BreakdownRow,
  type CallRow,
  type CandidateRow,
  type LlmUsageFilters,
  type ReconRow,
  type ResourceRecon,
} from "@/api/llm-usage";
import { AlertTriangle, ExternalLink, X } from "lucide-react";
import { useDocumentTitle } from "@/hooks/use-document-title";

/**
 * Platform-admin LLM usage & cost dashboard over `llm_calls` — one row per
 * model round, stamped with agent, workflow run, step, served model,
 * provider resource, pooled account and the cost at the time.
 *
 * Sections: filters → KPI strip → daily spend → where the money goes
 * (breakdown by dimension) → budgets → OAuth accounts → optimisation
 * candidates → reconciliation against Azure Monitor → call explorer.
 * Clicking a breakdown or candidate row narrows the filters; the call
 * explorer is the drill-down for everything.
 */

type Days = 7 | 30 | 90;

const DIM_LABELS: Record<BreakdownDim, string> = {
  tenant: "Tenant",
  agent: "Agent",
  workflow: "Workflow",
  step: "Step",
  model: "Model",
  resource: "Resource",
  account: "Account",
  lane: "Lane",
};

const KIND_LABELS: Record<CandidateRow["kind"], string> = {
  failures: "Failed calls",
  fallbacks: "Fallback models",
  "idle-background": "Idle background turns",
  "cache-write-heavy": "Cache-write heavy agents",
  "long-turns": "Long turns",
};

const FILTER_LABELS: Partial<Record<keyof LlmUsageFilters, string>> = {
  companyId: "Tenant",
  agentId: "Agent",
  workflowId: "Workflow",
  workflowRunId: "Run",
  workflowStepId: "Step",
  model: "Model",
  provider: "Provider",
  providerResource: "Resource",
  providerAccountId: "Account",
  lane: "Lane",
  outcome: "Outcome",
};

export function LlmUsage() {
  useDocumentTitle("LLM Usage");
  const [days, setDays] = useState<Days>(7);
  const [narrow, setNarrow] = useState<LlmUsageFilters>({});
  const [dim, setDim] = useState<BreakdownDim>("agent");

  const filters: LlmUsageFilters = useMemo(() => ({ days, ...narrow }), [days, narrow]);
  const filterKey = JSON.stringify(filters);

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ["llm-usage", "summary", filterKey],
    queryFn: () => llmUsageApi.summary(filters),
  });
  const { data: options } = useQuery({
    queryKey: ["llm-usage", "filters", days, narrow.companyId ?? ""],
    queryFn: () => llmUsageApi.filters({ days, companyId: narrow.companyId }),
  });
  const { data: breakdown, isLoading: breakdownLoading } = useQuery({
    queryKey: ["llm-usage", "breakdown", filterKey, dim],
    queryFn: () => llmUsageApi.breakdown(filters, dim),
  });
  const { data: candidates } = useQuery({
    queryKey: ["llm-usage", "candidates", filterKey],
    queryFn: () => llmUsageApi.candidates(filters),
  });
  const queryClient = useQueryClient();
  const budgetsQuery = useQuery({ queryKey: ["llm-usage", "budgets"], queryFn: () => llmUsageApi.budgets(), staleTime: 60_000 });
  const setBudget = useMutation({
    mutationFn: llmUsageApi.setBudget,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["llm-usage", "budgets"] }),
  });
  const runMonitor = useMutation({
    mutationFn: llmUsageApi.runBudgetMonitor,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["llm-usage", "budgets"] }),
  });
  const accountsQuery = useQuery({
    queryKey: ["llm-usage", "accounts", days, narrow.companyId ?? "", narrow.agentId ?? ""],
    queryFn: () => llmUsageApi.accounts({ days, companyId: narrow.companyId, agentId: narrow.agentId }),
    staleTime: 60_000,
  });
  const setAccountPlan = useMutation({
    mutationFn: llmUsageApi.setAccountPlan,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["llm-usage", "accounts"] }),
  });
  const reconQuery = useQuery({
    queryKey: ["llm-usage", "reconciliation", days, narrow.providerResource ?? ""],
    queryFn: () => llmUsageApi.reconciliation({ days, providerResource: narrow.providerResource }),
    staleTime: 5 * 60_000,
  });
  const callsQuery = useInfiniteQuery({
    queryKey: ["llm-usage", "calls", filterKey],
    queryFn: ({ pageParam }) => llmUsageApi.calls(filters, { limit: 100, before: pageParam ?? undefined }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextBefore,
  });
  const calls = useMemo(() => callsQuery.data?.pages.flatMap((p) => p.calls) ?? [], [callsQuery.data]);

  const set = (patch: LlmUsageFilters) =>
    setNarrow((prev) => {
      const next = { ...prev, ...patch };
      for (const k of Object.keys(next) as Array<keyof LlmUsageFilters>) {
        if (next[k] === undefined || next[k] === "" || next[k] === null) delete next[k];
      }
      return next;
    });
  const clear = (k: keyof LlmUsageFilters) => set({ [k]: undefined });

  const applyRow = (row: BreakdownRow) => {
    const m = row.meta;
    switch (dim) {
      case "tenant":
        return set({ companyId: m.companyId ?? undefined });
      case "agent":
        return set({ agentId: m.agentId ?? undefined, companyId: m.companyId ?? undefined });
      case "workflow":
        return set({ workflowId: m.workflowId ?? undefined, companyId: m.companyId ?? undefined });
      case "step":
        return set({ workflowId: m.workflowId ?? undefined, workflowStepId: m.workflowStepId ?? undefined, companyId: m.companyId ?? undefined });
      case "model":
        return set({ model: m.model ?? undefined, provider: m.provider ?? undefined });
      case "resource":
        return set({ providerResource: m.providerResource ?? undefined });
      case "account":
        return set({ providerAccountId: m.providerAccountId ?? undefined });
      case "lane":
        return set({ lane: m.lane ?? undefined });
    }
  };

  const applyCandidate = (c: CandidateRow) => {
    const m = c.meta;
    set({
      agentId: m.agentId ?? undefined,
      companyId: m.companyId ?? undefined,
      model: m.model ?? undefined,
      lane: m.lane ?? undefined,
      outcome: m.outcome ?? undefined,
      workflowRunId: m.workflowRunId ?? undefined,
    });
  };

  const t = summary?.totals;
  const activeFilters = Object.entries(narrow).filter(([, v]) => v);
  const labelFor = (k: string, v: string): string => {
    if (k === "companyId") return options?.tenants.find((x) => x.id === v)?.name ?? v.slice(0, 8);
    if (k === "agentId") return options?.agents.find((x) => x.id === v)?.name ?? v.slice(0, 8);
    if (k === "providerAccountId" || k === "workflowId" || k === "workflowRunId") return v.slice(0, 8);
    return v;
  };

  return (
    // The admin shell clips its column (overflow-hidden); this is the scroll
    // container, full width because the tables are wide.
    <div className="flex-1 overflow-y-auto">
      <div className="space-y-5 px-8 py-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">LLM Usage &amp; Cost</h2>
          <p className="text-sm text-text-secondary">
            Every model call the platform makes, attributed to tenant, agent, workflow and step. Cost is snapshotted at
            write time from the model catalogue.
          </p>
        </div>
        <Segmented<"7" | "30" | "90">
          aria-label="Window"
          value={String(days) as "7" | "30" | "90"}
          onChange={(v) => setDays(Number(v) as Days)}
          options={[
            { value: "7", label: "7d" },
            { value: "30", label: "30d" },
            { value: "90", label: "90d" },
          ]}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect
          label="Tenant"
          value={narrow.companyId ?? ""}
          onChange={(v) => set({ companyId: v || undefined, agentId: undefined })}
          options={(options?.tenants ?? []).map((x) => ({ value: x.id, label: x.name }))}
        />
        <FilterSelect
          label="Agent"
          value={narrow.agentId ?? ""}
          onChange={(v) => set({ agentId: v || undefined })}
          options={(options?.agents ?? [])
            .filter((a) => !narrow.companyId || a.companyId === narrow.companyId)
            .map((x) => ({ value: x.id, label: x.name }))}
        />
        <FilterSelect
          label="Model"
          value={narrow.model ?? ""}
          onChange={(v) => set({ model: v || undefined })}
          options={(options?.models ?? []).map((x) => ({ value: x.model, label: x.model }))}
        />
        <FilterSelect
          label="Resource"
          value={narrow.providerResource ?? ""}
          onChange={(v) => set({ providerResource: v || undefined })}
          options={(options?.resources ?? []).map((x) => ({ value: x, label: x }))}
        />
        <FilterSelect
          label="Lane"
          value={narrow.lane ?? ""}
          onChange={(v) => set({ lane: v || undefined })}
          options={(options?.lanes ?? []).map((x) => ({ value: x, label: x }))}
        />
        <FilterSelect
          label="Outcome"
          value={narrow.outcome ?? ""}
          onChange={(v) => set({ outcome: v || undefined })}
          options={[
            { value: "ok", label: "ok" },
            { value: "error", label: "error" },
          ]}
        />
        {activeFilters.map(([k, v]) => (
          <button
            key={k}
            type="button"
            onClick={() => clear(k as keyof LlmUsageFilters)}
            className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-[11px] text-accent"
            title="Remove filter"
          >
            {FILTER_LABELS[k as keyof LlmUsageFilters] ?? k}: {labelFor(k, String(v))}
            <X size={10} />
          </button>
        ))}
        {activeFilters.length > 0 && (
          <button type="button" onClick={() => setNarrow({})} className="text-[11px] text-text-muted hover:text-text-primary">
            Clear all
          </button>
        )}
      </div>

      {/* KPIs */}
      <KpiStrip
        loading={summaryLoading}
        items={[
          { label: "Cost", value: dollars(t?.costCents ?? 0), sub: t?.unpricedCalls ? `${t.unpricedCalls.toLocaleString()} unpriced calls` : "all calls priced", subTone: t?.unpricedCalls ? "warning" : "muted" },
          { label: "Calls", value: compact(t?.calls ?? 0), sub: `${compact(t?.turns ?? 0)} turns · ${t && t.turns ? (t.calls / t.turns).toFixed(1) : "0"} rounds/turn` },
          { label: "Input", value: compact(t?.inputTokens ?? 0), sub: "fresh prompt tokens" },
          { label: "Output", value: compact(t?.outputTokens ?? 0), sub: t?.reasoningTokens ? `${compact(t.reasoningTokens)} reasoning` : null },
          { label: "Cache read", value: compact(t?.cacheReadTokens ?? 0), sub: `${Math.round((t?.cacheHitShare ?? 0) * 100)}% of prompt from cache` },
          { label: "Cache write", value: compact(t?.cacheWriteTokens ?? 0), sub: `${compact(t?.cacheWritePerCall ?? 0)} per call`, subTone: (t?.cacheWritePerCall ?? 0) > 20_000 ? "warning" : "muted" },
          { label: "Errors", value: compact(t?.errors ?? 0), sub: t && t.calls ? `${((t.errors / t.calls) * 100).toFixed(1)}% of calls` : null, subTone: t?.errors ? "danger" : "muted" },
          { label: "Latency", value: `${((t?.avgLatencyMs ?? 0) / 1000).toFixed(1)}s`, sub: "mean per call" },
        ]}
      />

      {/* Daily */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Daily spend</CardTitle>
        </CardHeader>
        <CardContent>
          {!summary || summary.daily.every((d) => d.calls === 0) ? (
            <p className="text-sm text-text-muted">No calls in this window.</p>
          ) : (
            <DailyBars points={summary.daily} />
          )}
        </CardContent>
      </Card>

      {/* Breakdown */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Where the money goes</CardTitle>
          <Segmented<BreakdownDim>
            aria-label="Breakdown dimension"
            size="xs"
            value={dim}
            onChange={setDim}
            options={BREAKDOWN_DIMS.map((d) => ({ value: d, label: DIM_LABELS[d] }))}
          />
        </CardHeader>
        <CardContent>
          {breakdownLoading ? (
            <p className="text-sm text-text-muted">Loading...</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-text-muted">
                  <th className="py-2 pr-3 font-medium">{DIM_LABELS[dim]}</th>
                  <th className="py-2 pr-3 text-right font-medium">Calls</th>
                  <th className="py-2 pr-3 text-right font-medium">Turns</th>
                  <th className="py-2 pr-3 text-right font-medium">In</th>
                  <th className="py-2 pr-3 text-right font-medium">Out</th>
                  <th className="py-2 pr-3 text-right font-medium">Cache read</th>
                  <th className="py-2 pr-3 text-right font-medium">Cache write</th>
                  <th className="py-2 pr-3 text-right font-medium" title="Cache-write tokens per call: the prefix rebuilt each round">
                    CW/call
                  </th>
                  <th className="py-2 pr-3 text-right font-medium">Hit</th>
                  <th className="py-2 pr-3 text-right font-medium">Errors</th>
                  <th className="py-2 text-right font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {(breakdown?.rows ?? []).map((r) => (
                  <tr
                    key={r.key}
                    onClick={() => applyRow(r)}
                    className="cursor-pointer border-b border-border/40 hover:bg-surface-hover"
                    title="Click to filter"
                  >
                    <td className="py-2 pr-3 text-text-primary">{r.label}</td>
                    <Num v={r.calls} />
                    <Num v={r.turns} />
                    <Num v={r.inputTokens} muted />
                    <Num v={r.outputTokens} muted />
                    <Num v={r.cacheReadTokens} muted />
                    <Num v={r.cacheWriteTokens} muted />
                    <Num v={r.cacheWritePerCall} muted warn={r.cacheWritePerCall > 20_000} />
                    <td className="py-2 pr-3 text-right font-mono text-text-secondary">{Math.round(r.cacheHitShare * 100)}%</td>
                    <td className={"py-2 pr-3 text-right font-mono " + (r.errors ? "text-[#dc2626] dark:text-[#ef4444]" : "text-text-secondary")}>
                      {r.errors}
                    </td>
                    <td className="py-2 text-right font-mono text-text-primary">
                      {dollars(r.costCents)}
                      {r.unpricedCalls > 0 && (
                        <span className="ml-1 text-[10px] text-[#d97706]" title={`${r.unpricedCalls} calls have no catalogue price`}>
                          +{r.unpricedCalls}?
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {breakdown && breakdown.rows.length === 0 && (
                  <tr>
                    <td colSpan={11} className="py-4 text-center text-text-muted">
                      Nothing in this window.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Budgets */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            Budgets{" "}
            <span className="text-xs font-normal text-text-muted">
              {budgetsQuery.data?.month ?? ""} month to date · alerts at 50 / 80 / 100% · nothing is enforced
            </span>
          </CardTitle>
          <button
            type="button"
            onClick={() => runMonitor.mutate()}
            disabled={runMonitor.isPending}
            className="rounded-md border border-border px-2 py-1 text-[11px] text-text-secondary hover:bg-surface-hover disabled:opacity-50"
            title="Roll up spend and raise any crossed thresholds now instead of waiting for the 15-minute cycle"
          >
            {runMonitor.isPending ? "Running..." : "Run monitor now"}
          </button>
        </CardHeader>
        <CardContent>
          {budgetsQuery.isLoading ? (
            <p className="text-sm text-text-muted">Loading...</p>
          ) : (
            <BudgetTable
              rows={budgetsQuery.data?.rows ?? []}
              saving={setBudget.isPending}
              onSave={(row, cents) =>
                setBudget.mutate(row.scope === "agent" ? { agentId: row.agentId!, budgetMonthlyCents: cents } : { companyId: row.companyId, budgetMonthlyCents: cents })
              }
              onPick={(row) => set(row.scope === "agent" ? { companyId: row.companyId, agentId: row.agentId! } : { companyId: row.companyId, agentId: undefined })}
            />
          )}
          {budgetsQuery.data && budgetsQuery.data.alerts.length > 0 && (
            <div className="mt-3 text-[11px] text-text-muted">
              {budgetsQuery.data.alerts.length} alert{budgetsQuery.data.alerts.length === 1 ? "" : "s"} raised this month
              {budgetsQuery.data.alerts.some((a) => (a.delivered ?? []).some((d) => !d.ok)) ? " · some deliveries failed (see server log)" : ""}
              {budgetsQuery.data.alerts.every((a) => (a.delivered ?? []).length === 0) ? " · no delivery target configured: add a Teams webhook notification subscribed to budget.threshold, or set platform.supportWebhookUrl" : ""}
            </div>
          )}
        </CardContent>
      </Card>

      {/* OAuth accounts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            OAuth accounts{" "}
            <span className="text-xs font-normal text-text-muted">
              pooled Claude and ChatGPT subscriptions · cost is what the served calls would be at API price · fee prorated to the window
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {accountsQuery.isLoading ? (
            <p className="text-sm text-text-muted">Loading...</p>
          ) : (
            <AccountsTable
              rows={accountsQuery.data?.rows ?? []}
              saving={setAccountPlan.isPending}
              onSave={(accountId, patch) => setAccountPlan.mutate({ accountId, ...patch })}
              onPick={(r) => set({ providerAccountId: r.accountId, companyId: r.companyId })}
            />
          )}
        </CardContent>
      </Card>

      {/* Candidates */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle size={16} />
            Optimisation candidates
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!candidates || candidates.rows.length === 0 ? (
            <p className="text-sm text-text-muted">No failures, fallbacks, idle background turns or runaway turns in this window.</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {(Object.keys(KIND_LABELS) as CandidateRow["kind"][])
                .map((kind) => ({ kind, rows: candidates.rows.filter((r) => r.kind === kind) }))
                .filter((g) => g.rows.length > 0)
                .map((g) => (
                  <div key={g.kind}>
                    <div className="mb-1 text-xs font-medium text-text-muted">{KIND_LABELS[g.kind]}</div>
                    <ul className="divide-y divide-border/40">
                      {g.rows.map((c, i) => (
                        <li
                          key={i}
                          onClick={() => applyCandidate(c)}
                          className="flex cursor-pointer items-start justify-between gap-3 py-1.5 hover:bg-surface-hover"
                          title="Click to filter"
                        >
                          <div className="min-w-0">
                            <div className="truncate text-sm text-text-primary">{c.label}</div>
                            <div className="truncate text-[11px] text-text-muted">{c.detail}</div>
                          </div>
                          <div className="shrink-0 text-right font-mono text-xs text-text-secondary">
                            <div>{dollars(c.costCents)}</div>
                            <div className="text-text-muted">{compact(c.calls)} calls</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reconciliation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Reconciliation vs Azure Monitor{" "}
            <span className="text-xs font-normal text-text-muted">
              per deployment per UTC day · drift over {Math.round((reconQuery.data?.threshold ?? 0.02) * 100)}% flagged · Azure cost is its tokens at
              catalogue rates
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {reconQuery.isLoading ? (
            <p className="text-sm text-text-muted">Loading Azure Monitor...</p>
          ) : !reconQuery.data || reconQuery.data.resources.length === 0 ? (
            <p className="text-sm text-text-muted">No Foundry resources with calls in this window.</p>
          ) : (
            reconQuery.data.resources.map((r) => (
              <ReconResource
                key={r.resource}
                r={r}
                onPick={(row) => {
                  const m = options?.models.find((x) => x.model === row.deployment || x.model.endsWith("/" + row.deployment));
                  set({ providerResource: r.resource, model: m?.model, provider: undefined });
                }}
              />
            ))
          )}
        </CardContent>
      </Card>

      {/* Call explorer */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Calls{" "}
            <span className="text-xs font-normal text-text-muted">
              newest first · {calls.length.toLocaleString()} loaded
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border text-left text-text-muted">
                  <th className="py-2 pr-3 font-medium">When</th>
                  <th className="py-2 pr-3 font-medium">Agent</th>
                  <th className="py-2 pr-3 font-medium">Lane</th>
                  <th className="py-2 pr-3 font-medium">Workflow › step</th>
                  <th className="py-2 pr-3 font-medium">Model</th>
                  <th className="py-2 pr-3 font-medium">Resource</th>
                  <th className="py-2 pr-3 text-right font-medium">In</th>
                  <th className="py-2 pr-3 text-right font-medium">Out</th>
                  <th className="py-2 pr-3 text-right font-medium">CR</th>
                  <th className="py-2 pr-3 text-right font-medium">CW</th>
                  <th className="py-2 pr-3 text-right font-medium">Tools</th>
                  <th className="py-2 pr-3 text-right font-medium">ms</th>
                  <th className="py-2 pr-3 font-medium">Outcome</th>
                  <th className="py-2 pr-3 text-right font-medium">Cost</th>
                  <th className="py-2 font-medium">Request</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((c) => (
                  <CallLine key={c.id} c={c} />
                ))}
                {!callsQuery.isLoading && calls.length === 0 && (
                  <tr>
                    <td colSpan={15} className="py-4 text-center text-text-muted">
                      No calls match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {callsQuery.hasNextPage && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={() => callsQuery.fetchNextPage()}
                disabled={callsQuery.isFetchingNextPage}
                className="rounded-md border border-border px-3 py-1 text-xs text-text-secondary hover:bg-surface-hover disabled:opacity-50"
              >
                {callsQuery.isFetchingNextPage ? "Loading..." : "Load 100 more"}
              </button>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}

// ---------- pieces ----------

function AccountsTable({
  rows,
  saving,
  onSave,
  onPick,
}: {
  rows: AccountUsageRow[];
  saving: boolean;
  onSave: (accountId: string, patch: { plan?: string | null; monthlyFeeCents?: number | null }) => void;
  onPick: (r: AccountUsageRow) => void;
}) {
  const [editing, setEditing] = useState<{ id: string; field: "plan" | "fee" } | null>(null);
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  if (rows.length === 0) return <p className="text-sm text-text-muted">No pooled OAuth accounts.</p>;
  const health = (r: AccountUsageRow) => {
    if (r.status !== "active") return { label: r.status, cls: "text-text-muted" };
    if (r.cooling) {
      const mins = Math.max(1, Math.round(((r.cooldownUntil ?? 0) * 1000 - Date.now()) / 60_000));
      return { label: `cooling ${mins}m`, cls: "text-[#d97706] dark:text-[#f59e0b]" };
    }
    return { label: "active", cls: "text-[#16a34a] dark:text-[#22c55e]" };
  };
  const valueTone = (v: number | null) => (v == null ? "text-text-muted" : v >= 1 ? "text-[#16a34a] dark:text-[#22c55e]" : v >= 0.5 ? "text-text-primary" : "text-[#d97706] dark:text-[#f59e0b]");
  const commit = (r: AccountUsageRow) => {
    if (!editing) return;
    if (editing.field === "plan") onSave(r.accountId, { plan: draft.trim() === "" ? null : draft.trim() });
    else {
      const n = draft.trim() === "" ? null : Math.round(Number(draft) * 100);
      if (n === null || (Number.isFinite(n) && n >= 0)) onSave(r.accountId, { monthlyFeeCents: n });
    }
    setEditing(null);
  };
  const editable = (r: AccountUsageRow, field: "plan" | "fee", shown: string, muted: boolean) =>
    editing && editing.id === r.accountId && editing.field === field ? (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit(r);
          if (e.key === "Escape") setEditing(null);
        }}
        onBlur={() => setEditing(null)}
        placeholder={field === "plan" ? "e.g. Max 20x" : "$/month"}
        className="w-24 rounded border border-border bg-surface px-1 text-sm"
        disabled={saving}
      />
    ) : (
      <button
        type="button"
        onClick={() => {
          setEditing({ id: r.accountId, field });
          setDraft(field === "plan" ? (r.plan ?? "") : r.monthlyFeeCents == null ? "" : String(r.monthlyFeeCents / 100));
        }}
        className={"hover:underline " + (muted ? "text-text-muted" : "text-text-primary")}
        title={field === "plan" ? "Edit plan label" : "Edit monthly fee"}
      >
        {shown}
      </button>
    );
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-left text-xs text-text-muted">
          <th className="py-2 pr-3 font-medium">Account</th>
          <th className="py-2 pr-3 font-medium">Plan</th>
          <th className="py-2 pr-3 text-right font-medium" title="Flat monthly subscription fee; click to edit">Fee/mo</th>
          <th className="py-2 pr-3 font-medium">Health</th>
          <th className="py-2 pr-3 text-right font-medium">Calls</th>
          <th className="py-2 pr-3 text-right font-medium">In</th>
          <th className="py-2 pr-3 text-right font-medium">Out</th>
          <th className="py-2 pr-3 text-right font-medium">Cache read</th>
          <th className="py-2 pr-3 text-right font-medium" title="What these calls would cost at API list price">API-equiv.</th>
          <th className="py-2 pr-3 text-right font-medium" title="Fee prorated to the window">Fee (window)</th>
          <th className="py-2 pr-3 text-right font-medium" title="API-equivalent cost over prorated fee; under 1 means the API would be cheaper">Value</th>
          <th className="py-2 pr-3 text-right font-medium" title="Auth failures (401, expired token) / usage-limit failures (429, spend limit)">401 / limit</th>
          <th className="py-2 font-medium">Agents</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const h = health(r);
          const isOpen = open === r.accountId;
          return (
            <Fragment key={r.accountId}>
              <tr className="border-b border-border/40 hover:bg-surface-hover">
                <td className="py-2 pr-3">
                  <button type="button" onClick={() => onPick(r)} className="text-left text-text-primary hover:underline" title="Filter the page to this account">
                    {r.label ?? r.accountId.slice(0, 8)}
                  </button>
                  <div className="text-[11px] text-text-muted">
                    {r.companyName} · {r.provider}
                  </div>
                </td>
                <td className="py-2 pr-3 text-xs">{editable(r, "plan", r.plan ?? "set", r.plan == null)}</td>
                <td className="py-2 pr-3 text-right font-mono">{editable(r, "fee", r.monthlyFeeCents == null ? "set" : dollars(r.monthlyFeeCents), r.monthlyFeeCents == null)}</td>
                <td className={"py-2 pr-3 text-xs " + h.cls} title={r.lastError ?? undefined}>
                  {h.label}
                  {r.lastError && <span className="ml-1 text-text-muted">!</span>}
                </td>
                <Num v={r.calls} />
                <Num v={r.inputTokens} muted />
                <Num v={r.outputTokens} muted />
                <Num v={r.cacheReadTokens} muted />
                <td className="py-2 pr-3 text-right font-mono text-text-primary">
                  {dollars(r.costCents)}
                  {r.unpricedCalls > 0 && <span className="ml-1 text-[10px] text-[#d97706]">+{r.unpricedCalls}?</span>}
                </td>
                <td className="py-2 pr-3 text-right font-mono text-text-secondary">{r.feeForWindowCents == null ? "–" : dollars(r.feeForWindowCents)}</td>
                <td className={"py-2 pr-3 text-right font-mono " + valueTone(r.value)}>{r.value == null ? "–" : `${r.value.toFixed(1)}×`}</td>
                <td className={"py-2 pr-3 text-right font-mono " + (r.authErrors || r.limitErrors ? "text-[#dc2626] dark:text-[#ef4444]" : "text-text-secondary")}>
                  {r.authErrors} / {r.limitErrors}
                </td>
                <td className="py-2 text-xs">
                  {r.agents.length === 0 ? (
                    <span className="text-text-muted">–</span>
                  ) : (
                    <button type="button" onClick={() => setOpen(isOpen ? null : r.accountId)} className="text-text-secondary hover:underline">
                      {r.agents.length} agent{r.agents.length === 1 ? "" : "s"} {isOpen ? "▴" : "▾"}
                    </button>
                  )}
                </td>
              </tr>
              {isOpen &&
                r.agents.map((a) => (
                  <tr key={r.accountId + (a.agentId ?? a.agentSlug)} className="border-b border-border/20 text-xs text-text-secondary">
                    <td className="py-1 pl-4 pr-3" colSpan={4}>
                      ↳ {a.name}
                    </td>
                    <Num v={a.calls} small muted />
                    <td className="py-1 pr-3 text-right font-mono" colSpan={3}>
                      {compact(a.tokens)} tok
                    </td>
                    <td className="py-1 pr-3 text-right font-mono">{dollars(a.costCents)}</td>
                    <td colSpan={4} />
                  </tr>
                ))}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function BudgetTable({
  rows,
  saving,
  onSave,
  onPick,
}: {
  rows: BudgetRow[];
  saving: boolean;
  onSave: (row: BudgetRow, cents: number | null) => void;
  onPick: (row: BudgetRow) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const keyOf = (r: BudgetRow) => r.scope + ":" + (r.agentId ?? r.companyId);
  const tone = (pct: number | null) =>
    pct == null ? "text-text-muted" : pct >= 1 ? "text-[#dc2626] dark:text-[#ef4444]" : pct >= 0.8 ? "text-[#d97706] dark:text-[#f59e0b]" : "text-text-primary";
  if (rows.length === 0) return <p className="text-sm text-text-muted">No tenant or agent has a budget or spend this month.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-left text-xs text-text-muted">
          <th className="py-2 pr-3 font-medium">Scope</th>
          <th className="py-2 pr-3 font-medium">Name</th>
          <th className="py-2 pr-3 text-right font-medium">Spent</th>
          <th className="py-2 pr-3 text-right font-medium" title="Monthly budget in dollars; click to edit, blank clears">Budget</th>
          <th className="py-2 pr-3 text-right font-medium">Used</th>
          <th className="py-2 pr-3 text-right font-medium" title="Straight-line projection from the month-to-date run rate">Projected</th>
          <th className="py-2 font-medium">Alerts</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const k = keyOf(r);
          const isEditing = editing === k;
          return (
            <tr key={k} className="border-b border-border/40 hover:bg-surface-hover">
              <td className="py-2 pr-3 text-xs text-text-muted">{r.scope}</td>
              <td className="py-2 pr-3 text-text-primary">
                <button type="button" onClick={() => onPick(r)} className="text-left hover:underline" title="Filter the page to this scope">
                  {r.name}
                </button>
                {r.scope === "agent" && <span className="ml-1 text-[11px] text-text-muted">{r.companyName}</span>}
              </td>
              <td className="py-2 pr-3 text-right font-mono text-text-primary">
                {dollars(r.spentCents)}
                {r.unpricedCalls > 0 && (
                  <span className="ml-1 text-[10px] text-[#d97706]" title={`${r.unpricedCalls} calls have no catalogue price and count as $0`}>
                    +{r.unpricedCalls}?
                  </span>
                )}
              </td>
              <td className="py-2 pr-3 text-right font-mono">
                {isEditing ? (
                  <input
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const n = draft.trim() === "" ? null : Math.round(Number(draft) * 100);
                        if (n === null || (Number.isFinite(n) && n >= 0)) onSave(r, n);
                        setEditing(null);
                      }
                      if (e.key === "Escape") setEditing(null);
                    }}
                    onBlur={() => setEditing(null)}
                    placeholder="none"
                    className="w-24 rounded border border-border bg-surface px-1 text-right text-sm"
                    disabled={saving}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(k);
                      setDraft(r.budgetCents == null ? "" : String(r.budgetCents / 100));
                    }}
                    className={"hover:underline " + (r.budgetCents == null ? "text-text-muted" : "text-text-primary")}
                    title="Edit monthly budget"
                  >
                    {r.budgetCents == null ? "set" : dollars(r.budgetCents)}
                  </button>
                )}
              </td>
              <td className={"py-2 pr-3 text-right font-mono " + tone(r.pct)}>{r.pct == null ? "–" : `${Math.round(r.pct * 100)}%`}</td>
              <td className="py-2 pr-3 text-right font-mono text-text-secondary">{r.projectedCents == null ? "–" : dollars(r.projectedCents)}</td>
              <td className="py-2 text-xs text-text-muted">{r.alerted.length ? r.alerted.map((t) => `${t}%`).join(", ") : "–"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const STATUS_LABELS: Record<ReconRow["status"], { label: string; cls: string; title: string }> = {
  ok: { label: "ok", cls: "text-text-muted", title: "ProClaw and Azure agree within the threshold" },
  drift: { label: "drift", cls: "text-[#d97706] dark:text-[#f59e0b]", title: "ProClaw and Azure disagree: telemetry lost or double counted" },
  "azure-only": { label: "Azure only", cls: "text-[#dc2626] dark:text-[#ef4444]", title: "Azure served this but no ProClaw call recorded it: an unattributed caller" },
  "proclaw-only": { label: "ProClaw only", cls: "text-[#d97706] dark:text-[#f59e0b]", title: "ProClaw recorded calls Azure has no metric for (wrong resource or deployment name?)" },
  "no-azure": { label: "no Azure", cls: "text-text-muted", title: "Azure Monitor was not reachable for this resource" },
};

function ReconResource({ r, onPick }: { r: ResourceRecon; onPick: (row: ReconRow) => void }) {
  const [showAll, setShowAll] = useState(false);
  const limit = 28;
  const rows = showAll ? r.rows : r.rows.slice(0, limit);
  const t = r.totals;
  const pct = (v: number | null) => (v == null ? "n/a" : `${Math.round(v * 100)}%`);
  const covTone = (v: number | null) => (v == null ? "text-text-muted" : Math.abs(v - 1) <= 0.02 ? "text-[#16a34a] dark:text-[#22c55e]" : "text-[#d97706] dark:text-[#f59e0b]");
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="text-sm font-medium text-text-primary">{r.resource}</span>
        {r.azure.ok ? (
          <>
            <span className="text-xs text-text-muted">
              prompt coverage <span className={"font-mono " + covTone(t.promptCoverage)}>{pct(t.promptCoverage)}</span>
            </span>
            <span className="text-xs text-text-muted">
              output coverage <span className={"font-mono " + covTone(t.outputCoverage)}>{pct(t.outputCoverage)}</span>
            </span>
            <span className="text-xs text-text-muted">
              cost ProClaw <span className="font-mono text-text-primary">{dollars(t.proclaw.costCents)}</span> · Azure est.{" "}
              <span className="font-mono text-text-primary">{t.azureCostCents == null ? "n/a" : dollars(t.azureCostCents)}</span>
            </span>
          </>
        ) : (
          <span className="text-xs text-[#d97706] dark:text-[#f59e0b]" title={r.azure.error ?? undefined}>
            Azure Monitor unavailable{r.azure.error ? `: ${r.azure.error}` : ""}
            {r.azure.hint ? <span className="text-text-muted"> — {r.azure.hint}</span> : null}
          </span>
        )}
      </div>
      {r.rows.length === 0 ? (
        <p className="text-xs text-text-muted">No calls on either side.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th className="py-1.5 pr-3 font-medium">Day</th>
                <th className="py-1.5 pr-3 font-medium">Deployment</th>
                <th className="py-1.5 pr-3 text-right font-medium" title="ProClaw calls / Azure requests">Calls</th>
                <th className="py-1.5 pr-3 text-right font-medium" title="Fresh input tokens, ProClaw / Azure">Input</th>
                <th className="py-1.5 pr-3 text-right font-medium" title="Output tokens, ProClaw / Azure">Output</th>
                <th className="py-1.5 pr-3 text-right font-medium" title="Cache-read tokens, ProClaw / Azure">Cache read</th>
                <th className="py-1.5 pr-3 text-right font-medium" title="Cache-write tokens (5m + 1h), ProClaw / Azure">Cache write</th>
                <th className="py-1.5 pr-3 text-right font-medium" title="ProClaw snapshotted cost / Azure tokens at catalogue rates">Cost</th>
                <th className="py-1.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const st = STATUS_LABELS[row.status];
                return (
                  <tr
                    key={row.deployment + row.day}
                    onClick={() => onPick(row)}
                    className="cursor-pointer border-b border-border/40 hover:bg-surface-hover"
                    title="Click to filter the call explorer"
                  >
                    <td className="py-1.5 pr-3 font-mono text-text-secondary">{row.day}</td>
                    <td className="py-1.5 pr-3 text-text-primary">{row.deployment}</td>
                    <Pair p={row.proclaw.requests} a={row.azure?.requests} />
                    <Pair p={row.proclaw.inputTokens} a={row.azure?.inputTokens} />
                    <Pair p={row.proclaw.outputTokens} a={row.azure?.outputTokens} />
                    <Pair p={row.proclaw.cacheReadTokens} a={row.azure?.cacheReadTokens} />
                    <Pair p={row.proclaw.cacheWriteTokens} a={row.azure?.cacheWriteTokens} />
                    <td className="py-1.5 pr-3 text-right font-mono text-text-secondary">
                      {dollars(row.proclaw.costCents)}
                      {row.proclaw.unpricedCalls > 0 && <span className="text-[#d97706]">?</span>}
                      <span className="text-text-muted"> / {row.azureCostCents == null ? "n/a" : dollars(row.azureCostCents)}</span>
                    </td>
                    <td className={"py-1.5 " + st.cls} title={st.title}>
                      {st.label}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {r.rows.length > limit && (
            <button type="button" onClick={() => setShowAll((v) => !v)} className="mt-2 text-[11px] text-text-muted hover:text-text-primary">
              {showAll ? "Show fewer" : `Show all ${r.rows.length} rows`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** "ProClaw / Azure" token pair; the Azure figure is tinted when it differs by over 2%. */
function Pair({ p, a }: { p: number; a: number | undefined }) {
  const off = a != null && a > 0 && Math.abs(p - a) / a > 0.02;
  const offZero = a != null && a === 0 && p > 0;
  return (
    <td className="py-1.5 pr-3 text-right font-mono text-text-secondary">
      {compact(p)}
      <span className={a == null ? "text-text-muted" : off || offZero ? "text-[#d97706] dark:text-[#f59e0b]" : "text-text-muted"}> / {a == null ? "–" : compact(a)}</span>
    </td>
  );
}

function CallLine({ c }: { c: CallRow }) {
  const runHref =
    c.workflowRunId && c.workflowId ? `/tenants/${c.companyId}/workflows/${c.workflowId}/runs/${c.workflowRunId}` : null;
  const fallback = c.modelRequested && c.modelRequested !== c.model;
  return (
    <tr className="border-b border-border/40 align-top">
      <td className="whitespace-nowrap py-1.5 pr-3 font-mono text-text-secondary" title={c.ts}>
        {new Date(c.ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
        <span className="ml-1 text-text-muted">#{c.roundIndex}</span>
      </td>
      <td className="py-1.5 pr-3 text-text-primary">
        {c.agentSlug}
        {c.humanUpn && <div className="text-[10px] text-text-muted">{c.humanUpn}</div>}
      </td>
      <td className="py-1.5 pr-3">
        <Badge variant="secondary" className="text-[10px]">
          {c.lane ?? "?"}
        </Badge>
      </td>
      <td className="max-w-[220px] py-1.5 pr-3 text-text-secondary">
        {c.workflowRunId ? (
          <div className="flex items-center gap-1">
            <span className="truncate">
              {c.workflowName ?? "workflow"} › {c.workflowStepId ?? "(unstamped)"}
            </span>
            {runHref && (
              <Link to={runHref} className="shrink-0 text-accent" title="Open run">
                <ExternalLink size={11} />
              </Link>
            )}
          </div>
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </td>
      <td className="py-1.5 pr-3 text-text-secondary">
        {shortModel(c.model)}
        {fallback && (
          <div className="text-[10px] text-[#d97706]" title={`requested ${c.modelRequested}`}>
            fallback
          </div>
        )}
        {c.engine && c.engine !== "provider" && <div className="text-[10px] text-text-muted">{c.engine}</div>}
      </td>
      <td className="py-1.5 pr-3 text-text-muted">{c.providerResource ?? c.provider ?? "—"}</td>
      <Num v={c.inputTokens} muted small />
      <Num v={c.outputTokens} muted small />
      <Num v={c.cacheReadTokens} muted small />
      <Num v={c.cacheWriteTokens} muted small warn={c.cacheWriteTokens > 20_000} />
      <Num v={c.toolCalls} muted small />
      <Num v={c.latencyMs ?? 0} muted small />
      <td className="py-1.5 pr-3">
        {c.outcome === "error" ? (
          <span className="text-[#dc2626] dark:text-[#ef4444]" title={c.error ?? undefined}>
            error
          </span>
        ) : (
          <span className="text-text-muted">{c.stopReason ?? c.outcome ?? "ok"}</span>
        )}
      </td>
      <td className="whitespace-nowrap py-1.5 pr-3 text-right font-mono text-text-primary">
        {c.priced ? dollars(c.costCents ?? 0) : <span className="text-[#d97706]" title="no catalogue price for this model">?</span>}
      </td>
      <td className="py-1.5 font-mono text-[10px] text-text-muted" title={c.requestId ?? undefined}>
        {c.requestId ? c.requestId.slice(0, 12) : "—"}
      </td>
    </tr>
  );
}

function Num({ v, muted, warn, small }: { v: number; muted?: boolean; warn?: boolean; small?: boolean }) {
  return (
    <td
      className={
        (small ? "py-1.5" : "py-2") +
        " pr-3 text-right font-mono " +
        (warn ? "text-[#d97706] dark:text-[#f59e0b]" : muted ? "text-text-secondary" : "text-text-primary")
      }
    >
      {compact(v)}
    </td>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-[26px] max-w-[200px] rounded-md border border-border bg-surface px-2 text-[11.5px] text-text-secondary"
    >
      <option value="">{label}: all</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function DailyBars({ points }: { points: Array<{ day: string; calls: number; costCents: number; cacheWriteTokens: number; cacheReadTokens: number; errors: number }> }) {
  const maxCost = Math.max(1, ...points.map((p) => p.costCents));
  const maxCalls = Math.max(1, ...points.map((p) => p.calls));
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-[80px_1fr_72px_1fr_72px_72px] gap-2 border-b border-border pb-1 text-xs text-text-muted">
        <span>Day</span>
        <span>Cost</span>
        <span className="text-right">$</span>
        <span>Calls</span>
        <span className="text-right">Count</span>
        <span className="text-right">Errors</span>
      </div>
      {points.map((p) => (
        <div key={p.day} className="grid grid-cols-[80px_1fr_72px_1fr_72px_72px] items-center gap-2 text-xs">
          <span className="font-mono text-text-secondary">{p.day.slice(5)}</span>
          <div className="h-2 rounded bg-surface-hover">
            <div className="h-full rounded bg-accent/60" style={{ width: `${(p.costCents / maxCost) * 100}%` }} />
          </div>
          <span className="text-right font-mono text-text-secondary">{dollars(p.costCents)}</span>
          <div className="h-2 rounded bg-surface-hover">
            <div className="h-full rounded bg-accent/30" style={{ width: `${(p.calls / maxCalls) * 100}%` }} />
          </div>
          <span className="text-right font-mono text-text-secondary">{compact(p.calls)}</span>
          <span className={"text-right font-mono " + (p.errors ? "text-[#dc2626] dark:text-[#ef4444]" : "text-text-muted")}>{p.errors}</span>
        </div>
      ))}
    </div>
  );
}

// ---------- formatting ----------

function compact(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(0)}K`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
}

function dollars(cents: number): string {
  const d = cents / 100;
  if (d >= 1000) return `$${d.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (d >= 1) return `$${d.toFixed(2)}`;
  return `$${d.toFixed(3)}`;
}

function shortModel(m: string | null): string {
  if (!m) return "?";
  return m.replace(/^(azure-anthropic|anthropic|azure-openai|openai|openai-codex|claude-code)\//, "").replace(/^claude-/, "");
}
